import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';
import { ArrowLeft, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';

const NewTenantPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    billingEmail: '',
    billingName: '',
    planId: '',
    billingCycle: 'monthly',
    trialDays: 14
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await adminApi.getPlans();
      setPlans(response.data.data);
      // Set default plan (professional)
      const defaultPlan = response.data.data.find(p => p.isFeatured) || response.data.data[0];
      if (defaultPlan) {
        setFormData(prev => ({ ...prev, planId: defaultPlan.id }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      // Auto-generate slug from name
      if (name === 'name') {
        newData.slug = value
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '')
          .substring(0, 50);
      }
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await adminApi.createTenant(formData);
      toast.success('Tenant created successfully');
      navigate(`/tenants/${response.data.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create tenant');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = plans.find(p => p.id === formData.planId);

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center mb-6">
        <Link
          to="/tenants"
          className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create New Tenant</h1>
          <p className="text-gray-500 mt-1">Set up a new customer account</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="label">
                Company Name *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="input"
                placeholder="Acme Accounting"
              />
            </div>

            <div>
              <label htmlFor="slug" className="label">
                Slug *
              </label>
              <div className="flex items-center">
                <input
                  id="slug"
                  name="slug"
                  type="text"
                  required
                  value={formData.slug}
                  onChange={handleChange}
                  className="input rounded-r-none"
                  placeholder="acme"
                />
                <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg text-sm text-gray-500">
                  .practicemanagerpro.com
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                This will be the tenant's login URL
              </p>
            </div>
          </div>
        </div>

        {/* Billing Info */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Billing Information</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="billingEmail" className="label">
                Billing Email *
              </label>
              <input
                id="billingEmail"
                name="billingEmail"
                type="email"
                required
                value={formData.billingEmail}
                onChange={handleChange}
                className="input"
                placeholder="billing@acme.com"
              />
            </div>

            <div>
              <label htmlFor="billingName" className="label">
                Billing Contact Name
              </label>
              <input
                id="billingName"
                name="billingName"
                type="text"
                value={formData.billingName}
                onChange={handleChange}
                className="input"
                placeholder="John Smith"
              />
            </div>
          </div>
        </div>

        {/* Subscription */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription</h2>

          {/* Plan Selection */}
          <div className="mb-6">
            <label className="label mb-3">Select Plan *</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {plans.map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, planId: plan.id }))}
                  className={`p-4 rounded-lg border-2 text-left transition-colors ${
                    formData.planId === plan.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-primary-300'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{plan.name}</p>
                      {plan.isFeatured && (
                        <span className="badge-info text-xs mt-1">Recommended</span>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        ${formData.billingCycle === 'yearly' ? plan.priceYearly : plan.priceMonthly}
                      </p>
                      <p className="text-xs text-gray-500">
                        /{formData.billingCycle === 'yearly' ? 'year' : 'month'}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Billing Cycle */}
          <div className="mb-6">
            <label className="label mb-3">Billing Cycle</label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="billingCycle"
                  value="monthly"
                  checked={formData.billingCycle === 'monthly'}
                  onChange={handleChange}
                  className="mr-2"
                />
                Monthly
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="billingCycle"
                  value="yearly"
                  checked={formData.billingCycle === 'yearly'}
                  onChange={handleChange}
                  className="mr-2"
                />
                Yearly (Save 2 months)
              </label>
            </div>
          </div>

          {/* Trial Days */}
          <div>
            <label htmlFor="trialDays" className="label">
              Trial Period (days)
            </label>
            <select
              id="trialDays"
              name="trialDays"
              value={formData.trialDays}
              onChange={handleChange}
              className="input"
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={0}>No trial</option>
            </select>
          </div>
        </div>

        {/* Summary */}
        {selectedPlan && (
          <div className="card p-6 bg-gray-50">
            <h3 className="font-medium text-gray-900 mb-3">Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Plan</span>
                <span className="font-medium text-gray-900">{selectedPlan.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Price</span>
                <span className="font-medium text-gray-900">
                  ${formData.billingCycle === 'yearly' ? selectedPlan.priceYearly : selectedPlan.priceMonthly}
                  /{formData.billingCycle === 'yearly' ? 'year' : 'month'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Trial Period</span>
                <span className="font-medium text-gray-900">{formData.trialDays} days</span>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Link to="/tenants" className="btn-secondary">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Creating...
              </span>
            ) : (
              <>
                <Building2 className="w-4 h-4 mr-2" />
                Create Tenant
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewTenantPage;
